// The Funnel: where projects flow and where they stop, from first message to a
// picked talent. One sankey-style chart (hand-built SVG, like every chart in
// this console), a stat sidebar, and a numbers table, all over ONE aggregate
// payload (`GET /api/admin/funnel`) so every surface agrees.
//
// Interaction grammar:
// - Hovering (or keyboard-focusing) a node bar, a ribbon, a drop band, or the
//   self-serve lane opens the detail card; everything the card shows is ALSO
//   in the table below, so hover enhances and never gates.
// - Clicking a node, a drop, or the lane opens the Explorer's projects table
//   filtered to the matching stage badge (`?stage=`), because the badge
//   encodes "how far did it get", which is exactly what a funnel column is.
// - The cohort window is a URL param (`?window=30d`), so a filtered view is a
//   link you can send someone.
//
// Two series ride the whole chart, centered on a shared midline: SIGNED-IN
// owners (solid stage colors) and GUESTS (diagonal hatch, below, with a
// surface gap) — the legend in the chart header names them; guests who sign
// in move to the signed series. The searched stage forks in color: the
// concierge lane continues toward offers as a violet ribbon, and DIRECT picks
// (a seller chosen straight from the client's own search, no offer round)
// ride a cyan bypass band under the offers column into the picked bar. A
// second, budget-weighted funnel renders below, ending at the average offer.
import { useId, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import type { AdminFunnelResponse, FunnelStage } from "@recruiter/shared/types";

import { AreaBoundary } from "../components/area-gate";
import { EmptyState, LoadingState } from "../components/states";
import type { Area } from "../lib/areas";
import { BudgetFilter } from "../components/budget-filter";
import { CountryFilter } from "../components/country-filter";
import { DeviceFilter } from "../components/device-filter";
import { ExperimentFilter } from "../components/experiment-filter";
import type { ExperimentScope } from "../lib/experiment-scope";
import { useCanOpenExplorer } from "../lib/explorer-access";
import { useHoldsAnyArea } from "../lib/use-access";
import {
  formatDays,
  formatPct,
  formatUsd,
  formatUsdShort,
  FunnelChart as SharedFunnelChart,
  layoutFunnel,
  sharePct,
  TableExport,
  type FunnelHoverTarget,
  type FunnelLayout,
} from "@recruiter/shared";
import { formatClock, formatDateTime } from "../lib/format";
import {
  type DateRange,
  EMPTY_DATE_RANGE,
  DateRangeFilter,
  isDateRangeActive,
} from "@recruiter/shared";
import {
  FUNNEL_WINDOW_LABELS,
  FUNNEL_WINDOWS,
  type FunnelPresetWindow,
} from "../lib/funnel-window";
import { adminKeys, funnelQuery, funnelRangeGaveUp, funnelRangeQuery } from "../lib/queries";
import { dateRangeKey } from "../lib/date-range";
import { type BudgetRange, budgetKey, isBudgetActive } from "../lib/budget-range";
import "./funnel.css";

/** The other area a funnel tile can point at. Declared here so the two
 *  destinations read alike at the one place that decides whether a tile links. */
const TALENT_AREAS: readonly Area[] = ["talent_management"];

// Node-bar ink, one entry per stage, and the ONLY place this palette is written
// down. These were `.fn-node--*` CSS rules until the chart moved to
// @recruiter/shared, which is --rc-* only: a shared primitive cannot name this
// console's --ma-chart-* palette, so the palette comes to it as a resolved prop.
// The money funnel below is this page's own SVG and could have kept the CSS
// rules, but then one palette would live in two files, keyed by stage, and drift
// the first time a stage is added to one of them. It reads its fills from here
// too.
const STAGE_INK: Record<string, string> = {
  created: "var(--ma-chart-projects)",
  engaged: "var(--ma-chart-messages)",
  brief: "var(--ma-chart-signups)",
  // A deeper step of the same green, so the two brief stages read as one entity
  // progressing rather than two unrelated hues.
  brief_done: "color-mix(in srgb, var(--ma-chart-signups) 72%, var(--rc-color-text))",
  searched: "var(--ma-chart-searches)",
  offers: "var(--ma-chart-offers)",
  // Same treatment: reveal and pick as one entity progressing.
  finalists: "color-mix(in srgb, var(--ma-chart-picks) 62%, var(--ma-chart-searches))",
  picked: "var(--ma-chart-picks)",
};

// Funnel stage -> the Explorer stage-badge keys that mean "the journey ended
// here". The badge is the HIGHEST rung reached (repo/project_stage.py), so a
// node click lands on the projects currently sitting at that column.
//
// `finalists` and `picked` both pointed at `finalist` because the badge ladder
// had no rung for a pick — the reveal and the pick shared one. They are two
// rungs now, on the same two columns this chart already drew separately, so the
// two nodes no longer land on the same list.
const STAGE_TO_BADGE: Record<string, string> = {
  created: "new",
  engaged: "intake",
  brief: "brief",
  brief_done: "brief",
  searched: "search,concierge",
  offers: "offers",
  finalists: "finalist",
  picked: "picked",
};

interface TipState {
  x: number;
  y: number;
  title: string;
  rows: { value: string; label: string }[];
  hint: string | null;
}

/** Tooltip payload for one stage node. `index` 0 is the cohort itself. */
function nodeTip(
  data: AdminFunnelResponse,
  stage: FunnelStage,
  index: number,
): Omit<TipState, "x" | "y"> {
  const cohort = data.stages[0]?.reached ?? 0;
  const prev = index > 0 ? data.stages[index - 1] : undefined;
  const rows: { value: string; label: string }[] = [
    {
      value: String(stage.reached),
      label:
        index === 0
          ? "projects in this window"
          : `reached (${formatPct(sharePct(stage.reached, cohort))} of cohort)`,
    },
    {
      value: `${stage.reached - stage.guestReached} / ${stage.guestReached}`,
      label: "signed in / guest",
    },
  ];
  if (prev) {
    rows.push({
      value: formatPct(sharePct(stage.reached, prev.reached)),
      label: `of "${prev.label}" made it here`,
    });
  }
  const active = stage.stalled - stage.stalledArchived;
  rows.push({
    value: String(stage.stalled),
    label:
      stage.key === "picked"
        ? "ended here (the win)"
        : `stopped here (${stage.stalledArchived} archived, ${active} still sitting)`,
  });
  if (stage.avgBudgetUsd !== null && stage.avgBudgetUsd !== undefined) {
    rows.push({ value: formatUsd(stage.avgBudgetUsd), label: "avg committed budget here" });
  }
  if (stage.medianBudgetUsd !== null && stage.medianBudgetUsd !== undefined) {
    rows.push({ value: formatUsd(stage.medianBudgetUsd), label: "median committed budget here" });
  }
  if (stage.avgDaysFromCreation !== null && stage.avgDaysFromCreation !== undefined) {
    rows.push({ value: formatDays(stage.avgDaysFromCreation), label: "from creation, avg" });
  }
  if (stage.medianDaysFromCreation !== null && stage.medianDaysFromCreation !== undefined) {
    rows.push({ value: formatDays(stage.medianDaysFromCreation), label: "from creation, median" });
  }
  if (stage.key === "searched") {
    rows.push({
      value: `${data.lanes.concierge + data.lanes.both} / ${data.lanes.selfServe}`,
      label: "concierge / self-serve searches",
    });
  }
  if (stage.key === "offers") {
    const perProject =
      data.totals.avgOffersPerProject !== null && data.totals.avgOffersPerProject !== undefined
        ? data.totals.medianOffersPerProject !== null &&
          data.totals.medianOffersPerProject !== undefined
          ? ` (${data.totals.avgOffersPerProject} avg / ${data.totals.medianOffersPerProject} median per project)`
          : ` (${data.totals.avgOffersPerProject} per project)`
        : "";
    rows.push({
      value: String(data.totals.totalOffers),
      label: `offers in total${perProject}`,
    });
  }
  if (stage.key === "finalists") {
    const onward = data.transitions.find((t) => t.fromKey === "finalists" && t.toKey === "picked");
    if (onward) {
      rows.push({
        value: formatPct(sharePct(onward.count, stage.reached)),
        label: "of reveals became a pick",
      });
    }
  }
  if (stage.key === "picked") {
    const direct = data.transitions.find((t) => t.fromKey === "searched" && t.toKey === "picked");
    if (direct && direct.count > 0) {
      rows.push({
        value: String(direct.count),
        label: "picked straight from their own search",
      });
    }
  }
  return { title: stage.label, rows, hint: "Click to see these projects in the Explorer" };
}

/** The chart. The SVG itself is `@recruiter/shared`'s FunnelChart, shared with
 *  the BI app so the two never drift; this wrapper supplies the three things
 *  that are ours: the entity palette, the tooltip copy (which reads fields only
 *  this page's payload has, like per-stage timings), and where a click goes. */
function FunnelChart({
  data,
  onTip,
  onLeave,
}: {
  data: AdminFunnelResponse;
  onTip: (tip: Omit<TipState, "x" | "y">, event: React.PointerEvent | React.FocusEvent) => void;
  onLeave: () => void;
}) {
  const navigate = useNavigate();
  // Keyed as plain string: the shared chart works on untyped keys, so its
  // lookups must not narrow to the wire union.
  const byKey = useMemo(
    () =>
      new Map<string, { stage: FunnelStage; index: number }>(
        data.stages.map((s, i) => [s.key, { stage: s, index: i }]),
      ),
    [data.stages],
  );

  const openStage = (badge: string | undefined) => {
    void navigate({
      to: "/explorer",
      search: badge ? { view: "projects", stage: badge } : { view: "projects" },
    });
  };

  const tipFor = (target: FunnelHoverTarget): Omit<TipState, "x" | "y"> | null => {
    if (target.kind === "node") {
      const entry = byKey.get(target.key);
      return entry ? nodeTip(data, entry.stage, entry.index) : null;
    }
    if (target.kind === "drop") {
      const entry = byKey.get(target.key);
      if (!entry) return null;
      const tip = nodeTip(data, entry.stage, entry.index);
      return {
        title: `Stopped at "${entry.stage.label}"`,
        rows: [
          { value: String(target.stalled), label: "projects sitting here" },
          {
            value: `${entry.stage.stalled - entry.stage.guestStalled} / ${entry.stage.guestStalled}`,
            label: "signed in / guest",
          },
          ...tip.rows.filter(
            (r) =>
              r.label.startsWith("stopped here") ||
              r.label.startsWith("avg") ||
              r.label.startsWith("median"),
          ),
        ],
        hint: "Click to see these projects in the Explorer",
      };
    }
    if (target.kind === "bypass") {
      const edge = data.transitions.find((t) => t.fromKey === "searched" && t.toKey === "picked");
      return {
        title: "Picked from their own search",
        rows: [
          { value: String(target.count), label: "direct picks, no offer round" },
          {
            value: `${target.count - target.secondaryCount} / ${target.secondaryCount}`,
            label: "signed in / guest",
          },
          { value: formatDays(edge?.avgDays), label: "search to pick, avg" },
          { value: formatDays(edge?.medianDays), label: "search to pick, median" },
        ],
        hint: "Contacted a seller straight from the results. Click to see them.",
      };
    }
    const entry = byKey.get(target.fromKey);
    const from = entry?.stage.label ?? target.fromKey;
    const to = byKey.get(target.toKey)?.stage.label ?? target.toKey;
    const transition = data.transitions.find(
      (t) => t.fromKey === target.fromKey && t.toKey === target.toKey,
    );
    const conversion = entry ? sharePct(target.count, entry.stage.reached) : null;
    return {
      title: `${from} to ${to}`,
      rows: [
        { value: String(target.count), label: `made the step (${formatPct(conversion)})` },
        {
          value: `${target.count - target.secondaryCount} / ${target.secondaryCount}`,
          label: "signed in / guest",
        },
        { value: formatDays(transition?.avgDays), label: "between the two, avg" },
        { value: formatDays(transition?.medianDays), label: "between the two, median" },
      ],
      hint: target.fromKey === "searched" ? "The concierge lane carries this step" : null,
    };
  };

  const cohort = data.stages[0]?.reached ?? 0;
  const aria =
    `Funnel of ${cohort} projects, split into signed-in and guest owners: ` +
    data.stages
      .slice(1)
      .map((s) => `${formatPct(sharePct(s.reached, cohort))} ${s.label.toLowerCase()}`)
      .join(", ") +
    ". The searched stage forks into the concierge lane and self-serve-only projects.";

  return (
    <SharedFunnelChart
      stages={data.stages}
      transitions={data.transitions}
      colorForStage={(key) => STAGE_INK[key] ?? "var(--rc-color-text-muted)"}
      colorForBand={(fromKey, toKey) =>
        // Exactly two ribbons carry a meaning: searched->offers IS the concierge
        // lane, and the step into picked is the win. Every other ribbon stays
        // neutral flow ink so those two read.
        fromKey === "searched"
          ? "var(--ma-chart-searches)"
          : toKey === "picked"
            ? "var(--ma-chart-picks)"
            : undefined
      }
      bypassColor="var(--ma-chart-selfsearch)"
      unitLabel="projects"
      secondarySeriesLabel="guest"
      ariaLabel={aria}
      onHover={(target, event) => {
        const tip = tipFor(target);
        if (tip) onTip(tip, event);
      }}
      onLeave={onLeave}
      // A RIBBON has no stage to filter the Explorer by, so this page acts on
      // everything except one. Declared rather than silently ignored, or the
      // ribbons draw a pointer cursor and a button role over a dead click.
      isActivatable={(target) => target.kind !== "band"}
      // The affordance, spoken. A role="button" that does not say what it does
      // is worse than no button, and this page's marks carried this sentence
      // before the chart moved to @recruiter/shared.
      activateHint="Open in Explorer."
      onActivate={(target) => {
        if (target.kind === "bypass") return openStage("search");
        if (target.kind === "band") return;
        openStage(STAGE_TO_BADGE[target.key]);
      }}
    />
  );
}

/** The budget-weighted funnel: the same geometry engine fed committed-budget
 *  sums instead of project counts. One stream (no series split), money labels,
 *  hover detail, no clicks: it answers "where does the committed money park". */
function MoneyChart({
  data,
  layout,
  onTip,
  onLeave,
}: {
  data: AdminFunnelResponse;
  layout: FunnelLayout;
  onTip: (tip: Omit<TipState, "x" | "y">, event: React.PointerEvent | React.FocusEvent) => void;
  onLeave: () => void;
}) {
  const gradId = useId();
  const dropFade = `${gradId}-mdrop`;
  const laneFade = `${gradId}-mlane`;
  const guestHatch = `${gradId}-mhatch`;
  const cohortMoney = data.stages[0]?.sumBudgetUsd ?? 0;
  const byKey = useMemo(
    () => new Map<string, FunnelStage>(data.stages.map((s) => [s.key, s])),
    [data.stages],
  );
  const aria =
    `Committed budget flowing through the funnel: ${formatUsd(cohortMoney)} enters, ` +
    `${formatUsd(byKey.get("picked")?.sumBudgetUsd ?? 0)} reaches a picked talent. ` +
    `Average offer ${formatUsd(data.totals.avgOfferUsd)}, ` +
    `median ${formatUsd(data.totals.medianOfferUsd)}.`;

  return (
    <svg
      className="fn-svg fn-svg--money"
      viewBox={`0 0 ${layout.width} ${layout.height}`}
      role="img"
      aria-label={aria}
    >
      <defs>
        <linearGradient id={dropFade} x1="0" y1="0" x2="0.6" y2="1">
          <stop offset="0" style={{ stopColor: "var(--rc-color-text-muted)" }} stopOpacity={0.42} />
          <stop offset="1" style={{ stopColor: "var(--rc-color-text-muted)" }} stopOpacity={0.06} />
        </linearGradient>
        <linearGradient id={laneFade} x1="0" y1="0" x2="1" y2="0.4">
          <stop offset="0" style={{ stopColor: "var(--ma-chart-selfsearch)" }} stopOpacity={0.5} />
          <stop offset="1" style={{ stopColor: "var(--ma-chart-selfsearch)" }} stopOpacity={0.1} />
        </linearGradient>
        <pattern
          id={guestHatch}
          width="5"
          height="5"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <rect width="5" height="5" style={{ fill: "var(--rc-color-text-muted)" }} opacity={0.1} />
          <line
            x1="0"
            y1="0"
            x2="0"
            y2="5"
            style={{ stroke: "var(--rc-color-text-muted)" }}
            strokeWidth={1.7}
            opacity={0.55}
          />
        </pattern>
      </defs>

      {layout.drops.map((drop) => {
        const stage = byKey.get(drop.key);
        const tip = {
          title: `Budget parked at "${stage?.label ?? drop.key}"`,
          rows: [
            { value: formatUsdShort(drop.stalled), label: "committed by projects sitting here" },
            {
              value: formatPct(sharePct(drop.stalled, cohortMoney)),
              label: "of the cohort's money",
            },
          ],
          hint: null,
        };
        const dropHit = (d: string, first: boolean) => (
          <path
            className="fn-hit"
            d={d}
            tabIndex={first ? 0 : -1}
            aria-label={first ? `${tip.title}: ${formatUsdShort(drop.stalled)}` : undefined}
            onPointerMove={(e) => onTip(tip, e)}
            onFocus={(e) => onTip(tip, e)}
            onPointerLeave={onLeave}
            onBlur={onLeave}
          />
        );
        return (
          <g key={`m-drop-${drop.key}`}>
            {drop.signedPath ? (
              <path className="fn-drop" d={drop.signedPath} fill={`url(#${dropFade})`} />
            ) : null}
            {drop.guestPath ? (
              <path
                className="fn-drop fn-drop--guest"
                d={drop.guestPath}
                fill={`url(#${guestHatch})`}
              />
            ) : null}
            <text className="fn-drop-label" x={drop.labelX} y={drop.labelY + 3}>
              -{formatUsdShort(drop.stalled)}
              <tspan className="fn-drop-pct">
                {" "}
                {formatPct(sharePct(drop.stalled, stage?.sumBudgetUsd ?? 0))}
              </tspan>
            </text>
            {drop.signedPath ? dropHit(drop.signedPath, true) : null}
            {drop.guestPath ? dropHit(drop.guestPath, drop.signedPath === null) : null}
          </g>
        );
      })}

      {layout.bands.map((band) => {
        const from = byKey.get(band.fromKey);
        const to = byKey.get(band.toKey);
        const tip = {
          title: `${from?.label ?? band.fromKey} to ${to?.label ?? band.toKey}`,
          rows: [
            { value: formatUsdShort(band.count), label: "committed budget flows on" },
            {
              value: `${formatUsdShort(band.count - band.guestCount)} / ${formatUsdShort(band.guestCount)}`,
              label: "signed in / guest",
            },
            {
              value: formatPct(sharePct(band.count, cohortMoney)),
              label: "of the cohort's money",
            },
          ],
          hint: null,
        };
        return (
          <g key={`m-${band.fromKey}->${band.toKey}`}>
            <path
              className={`fn-band${band.toKey === "picked" ? " fn-band--picked" : ""}`}
              d={band.signedPath}
            />
            {band.guestPath ? (
              <path
                className="fn-band fn-band--guest"
                d={band.guestPath}
                fill={`url(#${guestHatch})`}
              />
            ) : null}
            <path
              className="fn-hit"
              d={band.signedPath}
              tabIndex={0}
              aria-label={`${tip.title}: ${formatUsdShort(band.count)}`}
              onPointerMove={(e) => onTip(tip, e)}
              onFocus={(e) => onTip(tip, e)}
              onPointerLeave={onLeave}
              onBlur={onLeave}
            />
            {band.guestPath ? (
              <path
                className="fn-hit"
                d={band.guestPath}
                tabIndex={-1}
                onPointerMove={(e) => onTip(tip, e)}
                onPointerLeave={onLeave}
              />
            ) : null}
          </g>
        );
      })}

      {/* The money bypass: committed budget of the direct picks. */}
      {layout.bypass
        ? (() => {
            const bypass = layout.bypass;
            const tip = {
              title: "Picked from their own search",
              rows: [
                { value: formatUsdShort(bypass.count), label: "committed budget, direct picks" },
                {
                  value: `${formatUsdShort(bypass.count - bypass.guestCount)} / ${formatUsdShort(bypass.guestCount)}`,
                  label: "signed in / guest",
                },
              ],
              hint: null,
            };
            const laneHit = (d: string) => (
              <path
                className="fn-hit"
                d={d}
                tabIndex={-1}
                onPointerMove={(e) => onTip(tip, e)}
                onPointerLeave={onLeave}
              />
            );
            return (
              <g>
                {bypass.signedPath ? (
                  <path className="fn-lane" d={bypass.signedPath} fill={`url(#${laneFade})`} />
                ) : null}
                {bypass.guestPath ? (
                  <path
                    className="fn-lane fn-lane--guest"
                    d={bypass.guestPath}
                    fill={`url(#${guestHatch})`}
                  />
                ) : null}
                <text className="fn-lane-label" x={bypass.labelX} y={bypass.labelY + 3}>
                  {formatUsdShort(bypass.count)} picked directly
                </text>
                {bypass.signedPath ? laneHit(bypass.signedPath) : null}
                {bypass.guestPath ? laneHit(bypass.guestPath) : null}
              </g>
            );
          })()
        : null}

      {layout.nodes.map((node, i) => {
        const stage = byKey.get(node.key);
        if (!stage) return null;
        const isFirst = i === 0;
        const isLast = i === layout.nodes.length - 1;
        const anchor = isFirst ? "start" : isLast ? "end" : "middle";
        const labelX = isFirst
          ? node.x
          : isLast
            ? node.x + layout.barWidth
            : node.x + layout.barWidth / 2;
        const nameY = Math.max(node.y - 26, 12);
        const countY = nameY + 17;
        const tip = {
          title: stage.label,
          rows: [
            { value: formatUsdShort(stage.sumBudgetUsd), label: "committed budget reached" },
            {
              value: `${formatUsdShort(stage.sumBudgetUsd - stage.guestSumBudgetUsd)} / ${formatUsdShort(stage.guestSumBudgetUsd)}`,
              label: "signed in / guest",
            },
            {
              value: formatPct(sharePct(stage.sumBudgetUsd, cohortMoney)),
              label: "of the cohort's money",
            },
            { value: formatUsd(stage.avgBudgetUsd), label: "avg per committed project" },
            { value: formatUsd(stage.medianBudgetUsd), label: "median per committed project" },
          ],
          hint: null,
        };
        return (
          <g key={`m-${node.key}`}>
            <rect
              className="fn-node"
              fill={STAGE_INK[node.key] ?? "var(--rc-color-text-muted)"}
              x={node.x}
              y={node.signed.y}
              width={layout.barWidth}
              height={Math.max(node.signed.h, stage.sumBudgetUsd > 0 ? 3 : 0)}
              rx={2}
            />
            {node.guest ? (
              <g>
                <rect
                  className="fn-node fn-seg--guest"
                  fill={STAGE_INK[node.key] ?? "var(--rc-color-text-muted)"}
                  x={node.x}
                  y={node.guest.y}
                  width={layout.barWidth}
                  height={node.guest.h}
                  rx={2}
                />
                <rect
                  className="fn-seg--guest-hatch"
                  x={node.x}
                  y={node.guest.y}
                  width={layout.barWidth}
                  height={node.guest.h}
                  rx={2}
                  fill={`url(#${guestHatch})`}
                />
              </g>
            ) : null}
            <text className="fn-node-label" x={labelX} y={nameY} textAnchor={anchor}>
              {node.label}
            </text>
            <text className="fn-node-count" x={labelX} y={countY} textAnchor={anchor}>
              {formatUsdShort(stage.sumBudgetUsd)}
            </text>
            <rect
              className="fn-hit"
              x={node.x - 14}
              y={Math.max(nameY - 14, 0)}
              width={layout.barWidth + 28}
              height={node.y + Math.max(node.h, 24) - Math.max(nameY - 14, 0)}
              tabIndex={0}
              aria-label={`${node.label}: ${formatUsdShort(stage.sumBudgetUsd)}`}
              onPointerMove={(e) => onTip(tip, e)}
              onFocus={(e) => onTip(tip, e)}
              onPointerLeave={onLeave}
              onBlur={onLeave}
            />
          </g>
        );
      })}
    </svg>
  );
}

function StatTile({
  label,
  value,
  median,
  sub,
  accent,
  to,
  search,
}: {
  label: string;
  value: string;
  /** The middle of the same population, for a tile whose `value` is a MEAN. Its
   *  own figure beside the average rather than a parenthetical in `sub`: on a
   *  spread as skewed as project budgets or time-to-offer the two differ a lot,
   *  and that gap is the reading. Pass "-" when the metric HAS a median that is
   *  unmeasured; omit it only where no median is defined. */
  median?: string;
  sub?: string;
  /** CSS color value for the tile's accent stripe. */
  accent?: string;
  to?: "/explorer" | "/talent-management/offers";
  search?: Record<string, string>;
}) {
  // A tile links out of the `funnel` area, and `funnel` implies nothing else
  // (migration 0115 grants numbers, not records). So a tile whose destination
  // this viewer cannot open stays a tile: same figure, no anchor. The number is
  // the point, and it is theirs; the drill-down is a bonus for whoever also
  // holds the area behind it.
  const canOpenExplorerPage = useCanOpenExplorer();
  const canOpenTalentPage = useHoldsAnyArea(TALENT_AREAS);
  const canFollow = to === "/explorer" ? canOpenExplorerPage : canOpenTalentPage;
  const style = accent ? ({ "--fn-accent": accent } as React.CSSProperties) : undefined;
  const body = (
    <>
      <span className="fn-tile__label">{label}</span>
      <span className="fn-tile__figures">
        <span className="fn-tile__value">{value}</span>
        {median === undefined ? null : (
          <span className="fn-tile__median">
            <span className="fn-tile__median-key">median</span>
            {median}
          </span>
        )}
      </span>
      {sub ? <span className="fn-tile__sub">{sub}</span> : null}
    </>
  );
  if (to && canFollow) {
    return (
      <Link to={to} search={search} className="fn-tile fn-tile--link" style={style}>
        {body}
      </Link>
    );
  }
  return (
    <div className="fn-tile" style={style}>
      {body}
    </div>
  );
}

/** The stage-by-stage numbers, always reachable without hovering anything. */
function FunnelTable({ data }: { data: AdminFunnelResponse }) {
  const cohort = data.stages[0]?.reached ?? 0;
  return (
    <details className="fn-details">
      <summary>Numbers as a table</summary>
      <div className="rc-table-wrap">
        <table className="rc-table">
          <TableExport name="funnel" label="the funnel" />
          <thead>
            <tr>
              <th>Stage</th>
              <th className="fn-num">Reached</th>
              <th className="fn-num">Guests</th>
              <th className="fn-num">Of cohort</th>
              <th className="fn-num">From previous</th>
              <th className="fn-num">Stopped here</th>
              <th className="fn-num">Archived</th>
              <th className="fn-num">Avg budget</th>
              <th className="fn-num">Median budget</th>
              <th className="fn-num">Committed $</th>
              <th className="fn-num">Avg days in</th>
              <th className="fn-num">Median days in</th>
            </tr>
          </thead>
          <tbody>
            {data.stages.map((s, i) => {
              const prev = i > 0 ? data.stages[i - 1] : undefined;
              return (
                <tr key={s.key}>
                  <td>{s.label}</td>
                  <td className="fn-num">{s.reached}</td>
                  <td className="fn-num">{s.guestReached}</td>
                  <td className="fn-num">
                    {i === 0 ? "-" : formatPct(sharePct(s.reached, cohort))}
                  </td>
                  <td className="fn-num">
                    {prev ? formatPct(sharePct(s.reached, prev.reached)) : "-"}
                  </td>
                  <td className="fn-num">{s.stalled}</td>
                  <td className="fn-num">{s.stalledArchived}</td>
                  <td className="fn-num">{formatUsd(s.avgBudgetUsd)}</td>
                  <td className="fn-num">{formatUsd(s.medianBudgetUsd)}</td>
                  <td className="fn-num">{formatUsdShort(s.sumBudgetUsd)}</td>
                  <td className="fn-num">{formatDays(s.avgDaysFromCreation)}</td>
                  <td className="fn-num">{formatDays(s.medianDaysFromCreation)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </details>
  );
}

function FunnelBoard({ data, dimmed }: { data: AdminFunnelResponse; dimmed: boolean }) {
  const [tip, setTip] = useState<TipState | null>(null);
  // The COUNT funnel's layout is computed inside the shared chart now; only the
  // money funnel, which is still this page's own SVG, lays itself out here.
  // The same stages, weighted by committed budget instead of project counts.
  const moneyLayout = useMemo(() => {
    const anyMoney = data.stages.some((s) => s.sumBudgetUsd > 0);
    if (!anyMoney) return null;
    const spine = data.stages.slice(1).map((s, i) => ({
      fromKey: data.stages[i]?.key ?? "created",
      toKey: s.key,
      count: s.sumBudgetUsd,
      guestCount: s.guestSumBudgetUsd,
    }));
    return layoutFunnel(
      data.stages.map((s) => ({
        key: s.key,
        label: s.label,
        reached: s.sumBudgetUsd,
        guestReached: s.guestSumBudgetUsd,
        stalled: s.stalledBudgetUsd,
        guestStalled: s.guestStalledBudgetUsd,
      })),
      [
        ...spine,
        // The money bypass: the committed budget of the direct picks.
        {
          fromKey: "searched",
          toKey: "picked",
          count: data.totals.directPickBudgetUsd,
          guestCount: data.totals.directPickGuestBudgetUsd,
        },
      ],
    );
  }, [data]);
  const totals = data.totals;
  const cohort = data.stages[0]?.reached ?? 0;

  // The one number a PM asks first: where do we lose the most projects?
  const worst = useMemo(() => {
    let best: { stage: FunnelStage; share: number } | null = null;
    for (const s of data.stages.slice(0, -1)) {
      if (s.stalled <= 0 || cohort <= 0) continue;
      const share = s.stalled / cohort;
      if (!best || share > best.share) best = { stage: s, share };
    }
    return best;
  }, [data.stages, cohort]);

  const canDrillIntoProjects = useCanOpenExplorer();
  const worstCallout = worst ? (
    <>
      <span className="fn-tile__label">Biggest drop-off</span>
      <span className="fn-callout__text">
        <strong>{worst.stage.label}</strong>: {worst.stage.stalled} of {cohort} projects stop here (
        {formatPct(worst.share * 100)}).
      </span>
    </>
  ) : null;

  const showTip = (
    partial: Omit<TipState, "x" | "y">,
    event: React.PointerEvent | React.FocusEvent,
  ) => {
    // The tip is positioned against the charts' shared column, so hovers on
    // the money funnel anchor correctly too.
    const host = (event.currentTarget as Element).closest(".fn-main");
    if (!host) return;
    const bounds = host.getBoundingClientRect();
    const pointer = "clientX" in event ? event : null;
    const targetBox = (event.currentTarget as Element).getBoundingClientRect();
    const rawX = pointer
      ? pointer.clientX - bounds.left
      : targetBox.left - bounds.left + targetBox.width / 2;
    const rawY = pointer ? pointer.clientY - bounds.top : targetBox.top - bounds.top;
    setTip({
      ...partial,
      x: Math.min(Math.max(rawX, 130), bounds.width - 130),
      y: Math.max(rawY - 12, 8),
    });
  };

  const offersStage = data.stages.find((s) => s.key === "offers");
  const pickedStage = data.stages.find((s) => s.key === "picked");
  const directPicks =
    data.transitions.find((t) => t.fromKey === "searched" && t.toKey === "picked")?.count ?? 0;
  const searchedBadge = STAGE_TO_BADGE.searched ?? "search,concierge";

  return (
    <div className={`fn-layout${dimmed ? " fn-layout--dim" : ""}`}>
      <div className="fn-main">
        <div className="fn-chart">
          <div className="fn-chart__head">
            <span className="fn-chart__caption">
              All projects in the window, by how far they got. Guests who sign in move to the
              signed-in flow as they progress.
            </span>
            <div className="fn-legend" aria-hidden="true">
              <span className="fn-legend__item">
                <span className="fn-legend__swatch fn-legend__swatch--signed" /> Signed in
              </span>
              <span className="fn-legend__item">
                <span className="fn-legend__swatch fn-legend__swatch--guest" /> Guest
              </span>
              <span className="fn-legend__item">
                <span className="fn-legend__swatch fn-legend__swatch--concierge" /> Concierge search
              </span>
              <span className="fn-legend__item">
                <span className="fn-legend__swatch fn-legend__swatch--lane" /> Direct pick
                (self-serve)
              </span>
            </div>
          </div>
          <FunnelChart data={data} onTip={showTip} onLeave={() => setTip(null)} />
        </div>

        {moneyLayout ? (
          <div className="fn-chart fn-chart--money">
            <div className="fn-chart__head">
              <span className="fn-chart__caption">
                The same funnel, weighted by committed budget
              </span>
              <div className="fn-legend" aria-hidden="true">
                <span className="fn-legend__item">
                  Avg offer <strong>{formatUsd(data.totals.avgOfferUsd)}</strong>
                </span>
                <span className="fn-legend__item">
                  Median offer <strong>{formatUsd(data.totals.medianOfferUsd)}</strong>
                </span>
                <span className="fn-legend__item">
                  Avg picked offer <strong>{formatUsd(data.totals.avgPickedOfferUsd)}</strong>
                </span>
                <span className="fn-legend__item">
                  Median picked offer <strong>{formatUsd(data.totals.medianPickedOfferUsd)}</strong>
                </span>
              </div>
            </div>
            <MoneyChart
              data={data}
              layout={moneyLayout}
              onTip={showTip}
              onLeave={() => setTip(null)}
            />
          </div>
        ) : null}
        {tip ? (
          <div className="fn-tip" style={{ left: tip.x, top: tip.y }}>
            <div className="fn-tip__title">{tip.title}</div>
            {tip.rows.map((row) => (
              <div key={`${row.value}-${row.label}`} className="fn-tip__row">
                <strong>{row.value}</strong> <span>{row.label}</span>
              </div>
            ))}
            {tip.hint ? <div className="fn-tip__hint">{tip.hint}</div> : null}
          </div>
        ) : null}
        <FunnelTable data={data} />
      </div>

      <aside className="fn-side" aria-label="Funnel statistics">
        {worst ? (
          // Same rule as the tiles: the finding is the content, the drill-down is
          // the bonus. Without `explorer` this stays the sentence it always was.
          canDrillIntoProjects ? (
            <Link
              to="/explorer"
              search={{ view: "projects", stage: STAGE_TO_BADGE[worst.stage.key] ?? "new" }}
              className="fn-callout fn-callout--link"
            >
              {worstCallout}
            </Link>
          ) : (
            <div className="fn-callout">{worstCallout}</div>
          )
        ) : null}
        <div className="fn-tiles">
          <StatTile
            label="Projects"
            value={String(totals.projects)}
            sub={`${totals.activeProjects} active, ${totals.archivedProjects} archived by clients`}
            accent="var(--ma-chart-projects)"
            to="/explorer"
            search={{ view: "projects" }}
          />
          <StatTile
            label="Reach offers"
            value={formatPct(sharePct(offersStage?.reached ?? 0, cohort))}
            sub={`${offersStage?.reached ?? 0} projects`}
            accent="var(--ma-chart-offers)"
            to="/talent-management/offers"
          />
          <StatTile
            label="Pick a talent"
            value={formatPct(sharePct(pickedStage?.reached ?? 0, cohort))}
            sub={`${pickedStage?.reached ?? 0} picked${
              directPicks > 0 ? `, ${directPicks} straight from self-serve` : ""
            }`}
            accent="var(--ma-chart-picks)"
            to="/explorer"
            search={{ view: "projects", stage: "finalist" }}
          />
          <StatTile
            label="Avg committed budget"
            value={formatUsd(totals.avgBudgetUsd)}
            median={formatUsd(totals.medianBudgetUsd)}
            sub={`${totals.budgetCommitted} of ${totals.projects} committed${
              totals.hourlyBudgets > 0 ? `, ${totals.hourlyBudgets} hourly` : ""
            }`}
            accent="var(--ma-chart-signups)"
          />
          <StatTile
            label="Time to first offer"
            value={formatDays(totals.avgDaysToFirstOffer)}
            median={formatDays(totals.medianDaysToFirstOffer)}
            sub="from project creation, avg"
            accent="var(--ma-chart-offers)"
            to="/talent-management/offers"
          />
          <StatTile
            label="Time to pick"
            value={formatDays(totals.avgDaysToPicked)}
            median={formatDays(totals.medianDaysToPicked)}
            sub="from project creation, avg"
            accent="var(--ma-chart-picks)"
          />
          <StatTile
            label="Offers per project"
            value={
              totals.avgOffersPerProject !== null && totals.avgOffersPerProject !== undefined
                ? String(totals.avgOffersPerProject)
                : "-"
            }
            median={
              totals.medianOffersPerProject !== null && totals.medianOffersPerProject !== undefined
                ? String(totals.medianOffersPerProject)
                : "-"
            }
            sub={`${totals.totalOffers} offers in total`}
            accent="var(--ma-chart-offers)"
            to="/talent-management/offers"
          />
          <StatTile
            label="Owners"
            value={formatPct(sharePct(totals.signedOwned, totals.projects))}
            sub={
              // `unclassifiedOwned` is projects left OUT of the whole cohort because
              // their owner's account was deleted before the guest bit was recorded
              // (backend migration 0150), so what they were is unrecoverable. Named
              // here rather than hidden: they used to be counted as signed in, which
              // is a wrong number that looks like a right one. Almost always 0.
              `signed in (${totals.signedOwned}), guests (${totals.guestOwned})` +
              (totals.unclassifiedOwned
                ? `, ${totals.unclassifiedOwned} excluded (owner deleted, cohort unknown)`
                : "")
            }
            accent="var(--ma-chart-messages)"
          />
          <StatTile
            label="Search lanes"
            value={`${data.lanes.concierge + data.lanes.both} / ${data.lanes.selfServe}`}
            sub={`concierge lane / self-serve only, ${data.lanes.both} used both`}
            accent="var(--ma-chart-searches)"
            to="/explorer"
            search={{ view: "projects", stage: searchedBadge }}
          />
        </div>
      </aside>
    </div>
  );
}

/** The four presets read a worker-materialized snapshot; a custom range is
 *  computed on demand. This hook hides that split from the page: it runs
 *  whichever of the two reads is live (the other is disabled, so no request is
 *  made for it) and hands back ONE shape.
 *
 *  `computing` is the state the preset lane simply does not have — a range
 *  nobody has asked for yet has to be built before it can be shown. */
function useFunnelView(
  window: FunnelPresetWindow,
  range: DateRange,
  scope: ExperimentScope | undefined,
  devices: readonly string[],
  countries: readonly string[],
  budget: BudgetRange,
) {
  // An A/B scope forces the on-demand lane even with no dates set: the four
  // presets are materialized for EVERYONE, and only the range aggregate takes
  // an arm. An unbounded range is the same cohort the `all` preset is, so the
  // reader loses nothing but the cache hit. A DEVICE selection forces it for
  // exactly the same reason — the presets are materialized across every device
  // — which is why "All devices" is the default and is genuinely free.
  // A budget bound is the same story again: the presets are materialized
  // across every budget, so a bound is an on-demand question.
  const custom =
    isDateRangeActive(range) ||
    scope !== undefined ||
    devices.length > 0 ||
    countries.length > 0 ||
    isBudgetActive(budget);
  const preset = useQuery({ ...funnelQuery(window), enabled: !custom });
  const ranged = useQuery({
    ...funnelRangeQuery(range, scope, devices, countries, budget),
    enabled: custom,
  });
  const active = custom ? ranged : preset;
  const data = custom ? (ranged.data?.funnel ?? null) : (preset.data ?? null);
  // How many times we have asked and been told `computing`. It lives on the
  // query STATE rather than the hook result, and it is read here rather than
  // counted in the component because the poll owns the cadence — a second
  // counter in React state would be a second, drifting answer to "how long have
  // we been waiting". Reading during render is safe: every poll response
  // re-renders this hook anyway.
  const client = useQueryClient();
  const polls =
    client.getQueryState(
      adminKeys.funnelRange(
        dateRangeKey(range),
        scope ? `${scope.experiment}:${scope.arm}` : "-",
        [...devices].sort().join("+") || "-",
        [...countries].sort().join("+") || "-",
        budgetKey(budget),
      ),
    )?.dataUpdateCount ?? 0;
  return {
    data,
    isError: active.isError,
    error: active.error,
    refetch: () => void active.refetch(),
    dimmed: active.isPlaceholderData,
    // Pending covers the first read; `computing` covers the worker still
    // building a range, which can outlast several fetches.
    isPending: active.isPending,
    computing: custom && !data && !active.isError,
    gaveUp: custom && funnelRangeGaveUp(ranged.data?.status, polls) && !ranged.data?.funnel,
  };
}

export function FunnelPage({
  window,
  onWindowChange,
  range,
  onRangeChange,
  scope,
  onScopeChange,
  devices,
  onDevicesChange,
  countries,
  onCountriesChange,
  budget,
  onBudgetChange,
}: {
  window: FunnelPresetWindow;
  onWindowChange: (next: FunnelPresetWindow) => void;
  range: DateRange;
  onRangeChange: (next: DateRange) => void;
  scope: ExperimentScope | undefined;
  onScopeChange: (next: ExperimentScope | undefined) => void;
  /** The devices the cohort is narrowed to. EMPTY = every device, the default. */
  devices: readonly string[];
  onDevicesChange: (next: readonly string[]) => void;
  /** The origin countries the cohort is narrowed to. EMPTY = every country. */
  countries: readonly string[];
  onCountriesChange: (next: readonly string[]) => void;
  /** The committed-budget bound, USD. Both sides open = no bound, the default. */
  budget: BudgetRange;
  onBudgetChange: (next: BudgetRange) => void;
}) {
  // `custom` drives the PRESET picker's disabled state, so it follows the same
  // rule the hook does: a scoped funnel is computed on demand, preset or not.
  const custom =
    isDateRangeActive(range) ||
    scope !== undefined ||
    devices.length > 0 ||
    countries.length > 0 ||
    isBudgetActive(budget);
  const view = useFunnelView(window, range, scope, devices, countries, budget);

  return (
    <div className="ad-page">
      <header className="ad-page__head">
        <h1 className="ad-page__title">Funnel</h1>
        <p className="ad-page__sub">
          Where projects flow and where they stop, from the first message to a picked talent. Hover
          any part for detail; click through to the projects behind a number.
        </p>
      </header>

      <div className="fn-windows" role="group" aria-label="Cohort window">
        {FUNNEL_WINDOWS.map((w) => (
          <button
            key={w}
            type="button"
            /* A preset is only ACTIVE while no custom range is set. Both lit at
               once would claim the chart is showing two different cohorts. */
            className={`fn-win${!custom && w === window ? " fn-win--active" : ""}`}
            aria-pressed={!custom && w === window}
            /* Picking a preset clears the range, because they are two answers to
               the same question and the range is the one that wins while set. */
            onClick={() => {
              onRangeChange(EMPTY_DATE_RANGE);
              onWindowChange(w);
            }}
          >
            {FUNNEL_WINDOW_LABELS[w]}
          </button>
        ))}
        <DateRangeFilter label="Cohort dates" value={range} onChange={onRangeChange} />
        <ExperimentFilter scope={scope} onChange={onScopeChange} />
        {/* What the projects were CREATED on (migration 0183). A creation stamp,
            like the cohort dates beside it — not where the client reads Mira
            today. Selecting one moves the page onto the on-demand lane. */}
        <DeviceFilter devices={devices} onChange={onDevicesChange} />
        {/* Where the projects were created FROM (migration 0195): the same
            creation-stamp reasoning, the same on-demand lane. */}
        <CountryFilter countries={countries} onChange={onCountriesChange} />
        {/* The projects' COMMITTED BUDGET, USD: a bound on the money funnel's
            own figure, applied before anything is counted. Same on-demand lane.
            A project with no readable budget stays in whatever the bound. */}
        <BudgetFilter budget={budget} onChange={onBudgetChange} />
        {/* These numbers are measured by the worker every few minutes, not on
            this request (backend/recruiter/funnel_snapshot.py). Say when, in
            the same breath as the cohort control: a cached figure whose age is
            invisible is one nobody can act on, and the reader would otherwise
            have no way to tell a stalled refresh from a flat week. */}
        {view.data ? (
          <span className="fn-stamp" title={formatDateTime(view.data.generatedAt)}>
            Updated {formatClock(view.data.generatedAt)}
          </span>
        ) : null}
      </div>

      {view.isPending && !view.computing ? <LoadingState label="Counting the cohort…" /> : null}
      {view.isError ? (
        <AreaBoundary area="funnel" error={view.error} onRetry={view.refetch} />
      ) : null}
      {/* A custom range is measured when it is asked for, which takes a few
          moments. Say that plainly rather than showing an empty chart: the
          reader is waiting on real work, not on a slow page. */}
      {view.computing && !view.gaveUp ? (
        <LoadingState label="Measuring these dates. This one is counted fresh, so give it a moment…" />
      ) : null}
      {view.gaveUp ? (
        <EmptyState
          title="Still counting"
          message="These dates are taking longer than expected. Try again, or pick a preset window."
          action={{ label: "Try again", onClick: view.refetch }}
        />
      ) : null}
      {view.data ? (
        view.data.totals.projects === 0 ? (
          <EmptyState
            title="No projects in this window"
            message="Try a wider window, or wait for the next project to arrive."
          />
        ) : (
          <FunnelBoard data={view.data} dimmed={view.dimmed} />
        )
      ) : null}
    </div>
  );
}
